import { AnyError } from '@microsoft/portal-app/lib/IAnyError';
import { useMutation, UseMutationResult, useQueryClient } from 'react-query';
import {
  getBatchUpdateUrl,
  IDirSyncStatusAndLanguagePreference,
} from '../../redux/modules/graphApis';
import { ILanguageEntry, ILanguageUpdateEntry, ISetDisplayLanguageBatchPayload } from '../models';
import { QueryKey } from '../models/QueryKey';
import { useAuth } from '@iamexperiences/feature-auth';
import {
  IRegionalAndLanguageSettingsQueryResult,
  useGetRegionalAndLanguageSettingsQuery,
} from './useGetRegionalAndLanguageSettingQuery';
import { ISetDataBatchResponse } from '../models/ISetDataBatchResponse';
import { isHttpSuccess } from '../utilities/isHttpSuccess';
import { getI18n } from 'react-i18next';
import { useReportAPITelemetry } from './useReportAPITelemetry';
import { HttpRequestError } from '../../shared/HttpRequestError';
import { getMSGraphScopeAndEndPoint } from '../../shared/utils';
import { MSGraphScopes } from '../../shared/constants';

export const useSetDisplayLanguageQuery = (): UseMutationResult<
  ISetDataBatchResponse<ISetDisplayLanguageBatchPayload> | undefined,
  AnyError,
  ILanguageEntry,
  unknown
> => {
  const { auth } = useAuth();
  const queryClient = useQueryClient();
  const { authoringLanguages } = useGetRegionalAndLanguageSettingsQuery();

  const { startBatchTelemetryReport, stopBatchTelemetryReport, stopTelemetryReport } =
    useReportAPITelemetry('setDisplayLanguage');

  return useMutation(async (language: ILanguageEntry) => {
    let newAuthoringLanguages = authoringLanguages;

    const languageIndex = authoringLanguages.findIndex(
      (item) => item.toLowerCase() === language.bcp47Tag.toLowerCase(),
    );

    if (languageIndex === -1) {
      newAuthoringLanguages = [language.bcp47Tag, ...newAuthoringLanguages];
    }

    const updateAuthoringLanguages: ILanguageUpdateEntry[] = newAuthoringLanguages.map((item) => {
      return { locale: item };
    });

    const url = getBatchUpdateUrl();
    const token = await auth.acquireToken({
      scopes: [getMSGraphScopeAndEndPoint(MSGraphScopes.UserReadWrite).scope],
    });

    const requests = [
      {
        id: '1',
        method: 'PATCH',
        url: '/me/preferredLanguage',
        body: {
          value: language.bcp47Tag,
        },
        headers: {
          'Content-Type': 'application/json',
        },
      },
      {
        id: '2',
        dependsOn: ['1'],
        method: 'PATCH',
        url: '/me/settings/regionalAndLanguageSettings',
        body: {
          authoringLanguages: updateAuthoringLanguages,
          defaultDisplayLanguage: {
            locale: language.bcp47Tag,
          },
        },
        headers: {
          'Content-Type': 'application/json',
        },
      },
    ];
    startBatchTelemetryReport(requests);

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: 'Bearer ' + token,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        requests,
      }),
    });

    if (!response.ok) {
      const error = new HttpRequestError(response.status, response.statusText);
      stopTelemetryReport('error', error);
      throw error;
    }

    const data = (await response.json()) as ISetDataBatchResponse<ISetDisplayLanguageBatchPayload>;
    const responses = data.responses;

    responses.forEach((item) => {
      stopBatchTelemetryReport(item.id, item);
    });

    const isSetDisplayLanguageSucceeded: boolean = isHttpSuccess(responses[0].status);
    const isUpdateSettingsSucceeded: boolean = responses.length > 1 && isHttpSuccess(responses[1].status);

    if (responses && isSetDisplayLanguageSucceeded) {
      getI18n().changeLanguage(language.bcp47Tag);
    }

    queryClient.setQueryData<IRegionalAndLanguageSettingsQueryResult | undefined>(
      QueryKey.REGIONAL_AND_LANGUAGE_SETTINGS,
      (old) => {
        if (old && isUpdateSettingsSucceeded) {
          return {
            ...old,
            authoringLanguages: newAuthoringLanguages,
          };
        }
        return old;
      },
    );

    queryClient.setQueryData<IDirSyncStatusAndLanguagePreference | undefined>(
      QueryKey.SYNC_STATUS_AND_LANGUAGE_PREFERENCE,
      (old: IDirSyncStatusAndLanguagePreference | undefined) => {
        if (old && isSetDisplayLanguageSucceeded) {
          return {
            ...old,
            preferredLanguage: language.bcp47Tag,
          };
        }
        return old;
      },
    );

    return data;
  });
};
